import type { GpcStatement } from "@/lib/gpc/gpcParser";
import {
  buildPohodaBankXml,
  encodeWindows1250,
  type PohodaBankMovement,
} from "@/lib/exports/pohoda";

/**
 * The free GPC → POHODA tool (`/gpc-do-pohody`): a Fio GPC statement's
 * movements as a POHODA XML import of bank movements (agenda Banka,
 * `bnk:bank`), built by the same builder as Zettl's own POHODA export and
 * validated against Stormware's schemas in CI (src/lib/exports/__tests__/
 * pohoda.test.ts). Pure and offline: it runs in the visitor's browser.
 */

export type GpcToPohodaError = "ico_invalid" | "account_invalid" | "no_movements";

export interface GpcToPohodaOptions {
  /** The company's IČO: POHODA imports a data pack only into the company with that IČO. */
  ico: string;
  /** The bank account's abbreviation in POHODA (e.g. "FIO"); optional, at most 19 characters. */
  account?: string;
}

export interface GpcToPohodaResult {
  /** Windows-1250 bytes, as Stormware's samples are encoded. */
  bytes: Uint8Array;
  xml: string;
  fileName: string;
  movements: number;
  /** `YYYY-MM` of the earliest movement. */
  period: string;
}

/** A Czech IČO: 8 digits with the mod-11 check digit (shorter ones padded with zeros). */
export function isValidIco(raw: string): boolean {
  const digits = raw.replace(/\s+/g, "");
  if (!/^\d{1,8}$/.test(digits)) return false;
  const ico = digits.padStart(8, "0");
  if (/^0+$/.test(ico)) return false;
  let sum = 0;
  for (let i = 0; i < 7; i += 1) sum += Number(ico[i]) * (8 - i);
  const check = (11 - (sum % 11)) % 10;
  return check === Number(ico[7]);
}

export function gpcToPohoda(
  statements: readonly GpcStatement[],
  options: GpcToPohodaOptions
): GpcToPohodaResult | { error: GpcToPohodaError } {
  const ico = options.ico.replace(/\s+/g, "");
  if (!isValidIco(ico)) return { error: "ico_invalid" };
  const account = (options.account ?? "").trim();
  if (account.length > 19 || /[<>&"]/.test(account)) return { error: "account_invalid" };

  const movements: PohodaBankMovement[] = [];
  for (const statement of statements) {
    for (const item of statement.items) {
      if (!item.postingDate || item.amount === 0) continue;
      movements.push({
        // Stable within the file: statement number, bank document id, position.
        id: `GPC-${statement.sequenceNumber ?? 0}-${item.documentId || "0"}-${movements.length + 1}`,
        date: item.postingDate,
        amount: item.amount,
        currency: "CZK",
        counterAccount: item.counterAccount,
        counterBankCode: item.counterBankCode,
        counterName: item.counterName || null,
        variableSymbol: item.variableSymbol,
        constantSymbol: item.constantSymbol,
        specificSymbol: item.specificSymbol,
        description: item.counterName || null,
      });
    }
  }
  if (movements.length === 0) return { error: "no_movements" };

  const period = movements.map((m) => m.date.slice(0, 7)).sort()[0];
  const xml = buildPohodaBankXml(
    { ico: ico.padStart(8, "0"), period, organizationId: ico },
    movements,
    account || null
  );
  return {
    xml,
    bytes: encodeWindows1250(xml),
    fileName: `pohoda-banka-${period}.xml`,
    movements: movements.length,
    period,
  };
}
